/**
 * Lo que la app de mostrador le pide a la API.
 *
 * Todo por `pedir` de `@fialrest/cliente`, que es el único cliente de red del
 * proyecto: manda las cookies —sin `credentials: 'include'` todo contesta 401— y
 * **valida cada respuesta contra su esquema**. Aquí eso importa más que en otras
 * pantallas: lo que llega de `/mostrador/cliente` se le enseña a una cajera que
 * va a actuar sobre ello con la fila esperando.
 *
 * ── Dos cookies, y ninguna se ve desde aquí ───────────────────────────────
 * `fialrest_caja` dura un año y dice qué aparato es; `fialrest_turno` dura cinco
 * minutos rodantes y es la que firma. Las dos son del servidor y este código no
 * las lee nunca: lo único que ve es el `401` y de qué tipo — «no es una caja»
 * manda al alta con SMS, «turno cerrado» manda al PIN.
 */
import { z } from 'zod'
import {
  esquemaCajaMontada, esquemaCanjeHecho, esquemaPinElegido, esquemaQuienPuedeEntrar,
  esquemaSelloHecho, esquemaTarjetaEnLaCaja, esquemaTurnoAbierto,
  type CajaMontada, type CanjeHecho, type PinElegido, type QuienPuedeEntrar, type SelloHecho,
  type TarjetaEnLaCaja, type TurnoAbierto,
} from '@fialrest/shared'
import { pedir } from '@fialrest/cliente'

/** Pide el SMS al teléfono de la empleada. La misma puerta que usa el panel. */
export const pedirCodigo = (telefono: string): Promise<{ encolado: true }> =>
  pedir('/autenticacion/codigo', z.object({ encolado: z.literal(true) }),
        { metodo: 'POST', cuerpo: { telefono } })

/** Convierte esta tablet en una caja. El token lo verificó Supabase, no nosotros. */
export const montarCaja = (
  cuerpo: { slug: string, token: string, pin?: string },
): Promise<CajaMontada> =>
  pedir('/mostrador/caja', esquemaCajaMontada, { metodo: 'POST', cuerpo })

/**
 * `T-70` · Elegir el PIN en una caja **ya montada**. No monta nada.
 *
 * La usa quien llega a una tablet que montó su compañera: hasta hoy su nombre
 * salía en gris y no había forma de activarse.
 */
export const elegirMiPin = (
  cuerpo: { slug: string, token: string, pin: string },
): Promise<PinElegido> =>
  pedir('/mostrador/mi-pin', esquemaPinElegido, { metodo: 'POST', cuerpo })

/** Quién puede entrar en esta caja. Nombres, nunca teléfonos. */
export const quienPuedeEntrar = (): Promise<QuienPuedeEntrar> =>
  pedir('/mostrador/personal', esquemaQuienPuedeEntrar)

/** Empezar turno: un nombre tocado y cuatro dígitos. */
export const abrirTurno = (staffUserId: string, pin: string): Promise<TurnoAbierto> =>
  pedir('/mostrador/turno', esquemaTurnoAbierto,
        { metodo: 'POST', cuerpo: { staffUserId, pin } })

/** Cerrar el turno. La caja se queda: cambiar de empleada son dos toques. */
export const cerrarTurno = (): Promise<void> =>
  pedir('/mostrador/turno', z.undefined(), { metodo: 'DELETE' })

/**
 * Encontrar a quien está delante, por el campo único.
 *
 * `POST` y no `GET` porque por aquí entra un teléfono, y **ningún dato personal
 * viaja en la URL**: una cadena de consulta acaba en los logs del proxy, en el
 * historial del navegador y en el `Referer` de la petición siguiente.
 */
export const buscarCliente = (entrada: string): Promise<TarjetaEnLaCaja> =>
  pedir('/mostrador/cliente', esquemaTarjetaEnLaCaja, { metodo: 'POST', cuerpo: { entrada } })

/**
 * `R-09` punto 1 · Sellar. **No pide nada más**: lo firma la sesión de turno.
 *
 * Teclear el PIN en cada sello no cabe en los cinco segundos y acabaría con el
 * código en un post-it pegado al monitor, que es peor que no tener PIN.
 */
export const sellar = (customerId: string): Promise<SelloHecho> =>
  pedir('/mostrador/sello', esquemaSelloHecho, { metodo: 'POST', cuerpo: { customerId } })

/**
 * `R-09` punto 2 · Canjear. **El PIN, siempre.**
 *
 * El `codigo` va si el comensal lo enseñó, y no es una declaración: el motor
 * intenta gastarlo y decide él la marca «sin verificación del cliente».
 */
export const canjear = (
  rewardId: string, pin: string, codigo?: string,
): Promise<CanjeHecho> =>
  pedir('/mostrador/canje', esquemaCanjeHecho,
        { metodo: 'POST', cuerpo: { rewardId, pin, ...(codigo ? { codigo } : {}) } })
